import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface Notebook {
  id: string;
  title: string;
  type: number;
  ownerUserId: string;
  tenantId: string;
  isShared: boolean;
  createdAt: string;
}

export interface Note {
  id: string;
  notebookId: string;
  title: string;
  contentHtml?: string | null;
  authorUserId: string;
  isPinned: boolean;
  tags?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export function useNotebooks() {
  return useQuery({
    queryKey: ["notebooks"],
    queryFn: async () => {
      const r = await api().get<Notebook[]>("/api/notebooks");
      return r.data;
    },
  });
}

export function useCreateNotebook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { title: string; isShared?: boolean }) => {
      const r = await api().post<Notebook>("/api/notebooks", data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notebooks"] });
    },
  });
}

export function useNotes(notebookId: string) {
  return useQuery({
    queryKey: ["notebooks", notebookId, "notes"],
    queryFn: async () => {
      const r = await api().get<Note[]>(`/api/notebooks/${notebookId}/notes`);
      return r.data;
    },
    enabled: !!notebookId,
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notebookId: string; title: string; contentHtml?: string }) => {
      const { notebookId, ...body } = data;
      const r = await api().post<Note>(`/api/notebooks/${notebookId}/notes`, body);
      return r.data;
    },
    onSuccess: (_, { notebookId }) => {
      qc.invalidateQueries({ queryKey: ["notebooks", notebookId, "notes"] });
    },
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notebookId: string; noteId: string; title?: string; contentHtml?: string; isPinned?: boolean }) => {
      const { notebookId, noteId, ...body } = data;
      const r = await api().put<Note>(`/api/notebooks/${notebookId}/notes/${noteId}`, body);
      return r.data;
    },
    onSuccess: (note) => {
      qc.invalidateQueries({ queryKey: ["notebooks", note.notebookId, "notes"] });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ notebookId, noteId }: { notebookId: string; noteId: string }) => {
      await api().delete(`/api/notebooks/${notebookId}/notes/${noteId}`);
      return { notebookId };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["notebooks", data.notebookId, "notes"] });
    },
  });
}
